/**
 * Geography hierarchy for the omni geography filter.
 *
 * Business data in AEMS is recorded against a shop; everything above a shop is a way of naming a
 * group of shops. The API ships the whole accessible tree in one call so selection, expansion,
 * search and redundancy checks all run locally.
 *
 * Naming: the API and this app say `cluster`. The database table is `area` for historical reasons —
 * that word must not appear in labels, types or analytics events.
 */

/** Ordered broadest → narrowest. The index in this array is the node's depth. */
export const LEVELS = ['region', 'cluster', 'country', 'location', 'shop'] as const;

export type Level = (typeof LEVELS)[number];

export interface GeoNode {
  code: string;
  name: string;
  parentCode: string | null;
}

export interface GeoScope {
  /** `true` when the user can see every shop in the system. Never use for permission logic. */
  isUnrestricted: boolean;
  /** Number of shops this user may filter on. `0` is a real state with its own empty view. */
  shopCount: number;
  /** Levels the user's access came from. Diagnostic only — do not surface in the picker. */
  grantedAt: Level[];
}

/** Raw `GET master-data/geography-tree` payload: five flat arrays, each node pointing one level up. */
export interface GeoTree {
  version: string;
  scope: GeoScope;
  regions: GeoNode[];
  clusters: GeoNode[];
  countries: GeoNode[];
  locations: GeoNode[];
  shops: GeoNode[];
}

/** Which array on the payload holds a given level. */
export const ARRAY_FOR: Record<Level, keyof Pick<GeoTree, 'regions' | 'clusters' | 'countries' | 'locations' | 'shops'>> = {
  region: 'regions',
  cluster: 'clusters',
  country: 'countries',
  location: 'locations',
  shop: 'shops',
};

/**
 * Builds a node's identity, which is always level-qualified: `` `${level}:${code}` ``.
 *
 * Every string used as a node identity — selection state, template keys, map lookups, analytics
 * events, URL params — must come from here, never a bare code. Codes are unique within a level, not
 * across levels: every shop code is three opaque alphanumeric characters drawn from the same shape
 * as region and cluster codes. There is no collision in production today, but nothing prevents the
 * next shop onboarding from creating one, and a code-keyed control would then silently select the
 * wrong node.
 */
export const nodeKey = (level: Level, code: string): string => `${level}:${code}`;

export const parseNodeKey = (key: string): { level: Level; code: string } => {
  const separator = key.indexOf(':');
  return { level: key.slice(0, separator) as Level, code: key.slice(separator + 1) };
};

export interface SearchEntry {
  key: string;
  level: Level;
  code: string;
  name: string;
  /** Lower-cased `code name`, precomputed so a keystroke is a substring test. */
  haystack: string;
}

/** Every `string` key below is a level-qualified node identity from {@link nodeKey}, never a code. */
export interface GeoIndex {
  version: string;
  scope: GeoScope;
  byKey: Map<string, { node: GeoNode; level: Level }>;
  parentOf: Map<string, string | null>;
  childrenOf: Map<string, string[]>;
  /** Nearest ancestor first, up to the region. Max length 4. */
  ancestorsOf: Map<string, string[]>;
  /** Shop codes reachable from any node, including shop nodes themselves. */
  shopsUnder: Map<string, string[]>;
  /** Keys at each level, in payload order (the API sorts by name). */
  keysByLevel: Map<Level, string[]>;
  searchable: SearchEntry[];
}

export interface ResultSection {
  level: Level;
  label: string;
  results: SearchEntry[];
  /** Total matched at this level, before the per-section cap. */
  matchCount: number;
}

export type Redundancy =
  | { kind: 'already-selected'; incoming: string }
  | { kind: 'covered-by'; incoming: string; by: string }
  | { kind: 'supersedes'; incoming: string; replaces: string[] }
  | null;

/** Singular label for a level — used on chips and rows. */
export const LEVEL_LABEL: Record<Level, string> = {
  region: 'Region',
  cluster: 'Cluster',
  country: 'Country',
  location: 'Location',
  shop: 'Shop',
};

/** Plural label for a level — used on typeahead section headers. */
export const LEVEL_LABEL_PLURAL: Record<Level, string> = {
  region: 'Regions',
  cluster: 'Clusters',
  country: 'Countries',
  location: 'Locations',
  shop: 'Shops',
};
